import type { ProjectArchiveRefusalReason } from "@/api/projectArchive";

/**
 * Wording for each stable PROJECT archive-refusal reason (#440, over #434).
 *
 * Deliberately its own map beside `archiveRefusal.ts` rather than a merge
 * into it: the contract carries `projectArchiveRefusalReason` as its own
 * field precisely because "the two vocabularies are different enums about
 * different resources", and one copy map serving both would re-fuse what the
 * contract split.
 *
 * `satisfies Partial<Record<…>>`, the house rule `archiveRefusal.ts` states
 * in full: a renamed or removed member becomes a compile error, while an
 * ADDED member still compiles and lands on the fallback — so the fallback
 * stays reachable and its test stays meaningful.
 */
const PROJECT_ARCHIVE_REFUSAL_COPY = {
  publication_in_flight:
    "A publication for this project is still in flight. Wait for it to merge or fail, then archive.",
} satisfies Partial<Record<ProjectArchiveRefusalReason, string>>;

/**
 * An unknown reason still refuses. The server said no; a missing label is a
 * copy gap, never a licence to re-offer the action.
 */
const UNLABELLED_REFUSAL = "The server will not change this project's archive state.";

/**
 * Our sentence for a reason we have copy for, or `null` — so each surface
 * picks its own fallback. A failed request is holding the server's own
 * sentence, which for an unlabelled reason is strictly more informative than
 * ours; `archiveRefusal.ts` argues the split at length.
 *
 * `(string & {})` admits a genuinely new server reason without erasing the
 * known members from autocomplete.
 */
export function projectArchiveRefusalKnownCopy(
  reason: ProjectArchiveRefusalReason | (string & {}),
): string | null {
  return (
    PROJECT_ARCHIVE_REFUSAL_COPY[reason as ProjectArchiveRefusalReason] ?? null
  );
}

/** The clinician-facing sentence for a reason the server named. */
export function projectArchiveRefusalCopy(
  reason: ProjectArchiveRefusalReason | (string & {}),
): string {
  return projectArchiveRefusalKnownCopy(reason) ?? UNLABELLED_REFUSAL;
}
